import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { EditLeadDetails } from "../Services/RecruitmentService";
import { GetLeadDetailsByUID } from "../Services/RecruitmentService";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import {
  Mail, Phone, User, MapPin, Map, Globe,
  Hash, Link, Briefcase, GraduationCap,
  Building, Award, ClipboardList, Target,
  CreditCard, UserCheck, FileCheck, Upload,
  FileText, Eye
} from "lucide-react";
import { unformatNumber, formatCTC } from "../../../../Utils/FormUtils";
import ViewResumeDrawer from "../../../../Common/Recruitment/Modals/ViewResumeDrawer";

const InputField = ({
  label,
  value,
  onChange,
  icon: Icon,
  readOnly,
  type = "text",
  placeholder,
  error,
  required,
}) => (
  <div className="space-y-1">
    <label className="text-sm font-semibold text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>

    <div
      className={`flex items-center border rounded-xl px-3 py-2 shadow-sm transition
                ${error ? "border-red-500" : "border-border"}
                ${
                  readOnly
                    ? "bg-gray-100 border-border cursor-not-allowed opacity-80"
                    : "bg-white focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary"
                }
      `}
    >
      {Icon && (
        <Icon
          className={`mr-2 ${readOnly ? "text-gray-400" : "text-gray-500"}`}
          size={18}
        />
      )}
      <input
        disabled={readOnly}
        type={type}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`w-full bg-transparent outline-none text-sm 
                    ${readOnly ? "cursor-not-allowed  text-gray-500 select-none" : "text-gray-800"}`}
      />
    </div>

    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const EditLead = () => {
  const isBasicReadOnly = true;
  const { leadUID } = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  const targetOrganization = location?.state?.targetOrganization || "";
  const targetRole = location?.state?.targetRole || "";

  const [form, setForm] = useState({ remark: "" });
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [keepExistingResume, setKeepExistingResume] = useState(false);
  const [openResumeDrawer, setOpenResumeDrawer] = useState(false);

  useEffect(() => {
    if (leadUID) {
      fetchLead(leadUID);
    }
  }, [leadUID]);

  const fetchLead = async (leadUID) => {
    try {
      const res = await GetLeadDetailsByUID(leadUID);
      const basic = res?.leadDetails?.[0] || {};
      const professional = res?.recruitmentDetails?.[0] || {};
      const resumePath = professional.resumePath || "";

      setForm({
        // BASIC
        name: basic.name || "",
        email: basic.email || "",
        phoneNo: basic.phoneNo || "",
        gender: basic.gender || "",
        city: basic.city || "",
        state: basic.state || "",
        country: basic.country || "",
        pinCode: basic.pinCode || "",
        source: basic.source || "",

        // PROFESSIONAL
        currentLocation: professional.currentLocation || "",
        qualification: professional.qualification || "",
        totalExperience: professional.totalExperience || "",
        currentCompany: professional.currentCompany || "",
        designation: professional.designation || "",
        skills: professional.skill || "",
        appliedFor: professional.appliedFor || "",
        preferredLocation: professional.preferredLocation || "",
        currentCTC: formatCTC(professional.currentCTC || ""),
        expectedCTC: formatCTC(professional.expectedCTC || ""),
        reasonOfLeaving: professional.reasonOfLeaving || "",
        department: professional.department || "",
        resumePath: resumePath,
        remark: ""
      });

      setKeepExistingResume(!!resumePath);
    } catch (err) {
      setAlert({ type: "error", message: err?.message || "Server error" });
    }
  };

  const handleChange = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  const ValidateForm = () => {
    const errors = {};

    const isEmpty = (val) => !val || val.trim() === "";
    const isValidDecimal = (val) => /^[0-9]+(\.[0-9]{1,2})?$/.test(val);

    // Experience
    if (!isEmpty(form.totalExperience)) {
      if (!isValidDecimal(form.totalExperience)) {
        errors.totalExperience = "Total experience must be a numeric value.";
      } else if (Number(form.totalExperience) >= 100) {
        errors.totalExperience = "Please enter a valid experience value.";
      }
    }

    // CTC
    const cleanCurrentCTC = unformatNumber(form.currentCTC || "");
    const cleanExpectedCTC = unformatNumber(form.expectedCTC || "");
    const hasExperience = Number(form.totalExperience) > 0;

    // Designation
    if (hasExperience) {
      if (isEmpty(form.designation)) {
        errors.designation = "Please enter the designation.";
      }
    }

    // Company
    if (hasExperience && isEmpty(form.currentCompany)) {
      errors.currentCompany = "Please enter the current company.";
    }

    // Current CTC
    if (hasExperience) {
      if (isEmpty(form.currentCTC)) {
        errors.currentCTC = "Please enter the current CTC.";
      } else if (!/^\d+$/.test(cleanCurrentCTC)) {
        errors.currentCTC = "Current CTC must be a numeric value.";
      }
    }

    // Expected CTC
    if (hasExperience) {
      if (isEmpty(form.expectedCTC)) {
        errors.expectedCTC = "Please enter the expected CTC.";
      } else if (!/^\d+$/.test(cleanExpectedCTC)) {
        errors.expectedCTC = "Expected CTC must be a numeric value.";
      } else if (Number(cleanExpectedCTC) < Number(cleanCurrentCTC)) {
        errors.expectedCTC =
          "Expected CTC should be greater than or equal to current CTC.";
      }
    }

    // Reason of Leaving
    if (hasExperience && isEmpty(form.reasonOfLeaving)) {
      errors.reasonOfLeaving = "Please enter the reason for leaving.";
    }

    if(isEmpty(form.remark)) {
      errors.remark = "Please enter Remark"
    }
    return errors;
  };

  const handleUpdate = async () => {
    const validationErrors = ValidateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("LeadUID", leadUID);
      formData.append("CurrentLocation", form.currentLocation || "");
      formData.append("Qualification", form.qualification || "");
      formData.append("TotalExperience", form.totalExperience || "");
      formData.append("CurrentCompany", form.currentCompany || "");
      formData.append("Designation", form.designation || "");
      formData.append("Skills", form.skills || "");
      formData.append("AppliedFor", form.appliedFor || "");
      formData.append("PreferredLocation", form.preferredLocation || "");
      formData.append(
        "CurrentCTC",
        form.currentCTC ? Number(unformatNumber(form.currentCTC)) : "",
      );

      formData.append(
        "ExpectedCTC",
        form.expectedCTC ? Number(unformatNumber(form.expectedCTC)) : "",
      );

      formData.append("ReasonOfLeaving", form.reasonOfLeaving || "");
      formData.append("Department", form.department || "");
      if (form.resumeFile) {
        formData.append("Resume", form.resumeFile);
      }
      formData.append("KeepExistingResume", keepExistingResume);
      formData.append("TargetOrganization", targetOrganization || "");
      formData.append("TargetRole", targetRole || "");
      formData.append("CallResponse", "CONNECTED");
      formData.append("Remark", form.remark);
      const res = await EditLeadDetails(formData);
      if (res?.status === "success") 
      {
        setAlert({ type: "success", message: res?.message });
        setErrors({});

        setForm({});
        setTimeout(() => {
          navigate("/my-leads", { replace: true });
        }, 1500);
      } else {
        setAlert({ type: "error", message: res?.message });
      }
    } catch (error) {
      setAlert({ type: "error", message: error?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCTCChange = (key) => (e) => {
    let value = e.target.value;

    // remove commas first
    value = unformatNumber(value);

    // allow only numbers
    if (!/^\d*$/.test(value)) return;

    setForm((prev) => ({
      ...prev,
      [key]: formatCTC(value),
    }));
  };

  return (
    <>
      <div className=" p-6">
        <div className="max-w-8xl mx-auto mb-4">
          <div className="flex items-center justify-between bg-white border border-border rounded-xl px-6 py-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-orange-500 animate-pulse"></div>
              <span className="text-sm font-semibold text-gray-700">
                Editing Lead Profile
              </span>
            </div>

            <span className="text-xs text-gray-500">
              Changes will be saved on update
            </span>
          </div>
        </div>
        {/* BASIC DETAILS */}
        <div className="max-w-8xl mx-auto rounded-2xl overflow-hidden bg-white border border-border transition shadow-md">
          <div className="relative overflow-hidden border-b border-slate-200 bg-white px-8 py-6">
            <div className="absolute top-0 right-0 h-32 w-32 rounded-full bg-blue-100 blur-3xl opacity-60"></div>

            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 shadow-sm">
                  <UserCheck className="text-blue-600" size={28} />
                </div>

                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-slate-800">
                      Basic Lead Details
                    </h2>

                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">
                      CRM
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-slate-500">
                    Update candidate information and recruitment details
                  </p>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-sm font-medium text-slate-600">
                  Recruitment CRM
                </span>
              </div>
            </div>
          </div>
          <div className="px-8 pt-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
              <UserCheck size={16} />
              Basic details are locked and cannot be edited
            </div>
          </div>
          <div className="p-8 grid md:grid-cols-3 gap-6">
            <InputField
              label="Full Name"
              icon={User}
              value={form.name}
              onChange={handleChange("name")}
              placeholder="John Doe"
              readOnly={isBasicReadOnly}
            />

            <InputField
              label="Email"
              icon={Mail}
              value={form.email}
              onChange={handleChange("email")}
              placeholder="john@email.com"
              readOnly={isBasicReadOnly}
            />

            <InputField
              label="Phone"
              icon={Phone}
              value={form.phoneNo}
              readOnly={isBasicReadOnly}
              placeholder="+91 9876543210"
            />

            {/* Gender */}
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700">
                Gender
              </label>

              <div
                className={`flex items-center border rounded-xl px-3 py-2
                            ${
                              isBasicReadOnly
                                ? "bg-gray-100 border-border cursor-not-allowed"
                                : "bg-white hover:border-gray-300 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary"
                            }
                          `}
              >
                <User
                  className={`mr-2 ${
                    isBasicReadOnly ? "text-gray-400" : "text-gray-500"
                  }`}
                  size={18}
                />

                <select
                  value={form.gender || ""}
                  onChange={handleChange("gender")}
                  disabled={isBasicReadOnly}
                  className={`w-full bg-transparent outline-none text-sm appearance-none ${
                    isBasicReadOnly
                      ? "text-gray-500 cursor-not-allowed"
                      : "text-gray-700"
                  }`}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>

            <InputField
              label="City"
              icon={MapPin}
              value={form.city}
              onChange={handleChange("city")}
              placeholder="Mumbai"
              readOnly={isBasicReadOnly}
            />

            <InputField
              label="State"
              icon={Map}
              value={form.state}
              onChange={handleChange("state")}
              placeholder="Maharashtra"
              readOnly={isBasicReadOnly}
            />

            <InputField
              label="Country"
              icon={Globe}
              value={form.country}
              onChange={handleChange("country")}
              placeholder="India"
              readOnly={isBasicReadOnly}
            />

            <InputField
              label="Pin Code"
              icon={Hash}
              value={form.pinCode}
              onChange={handleChange("pinCode")}
              placeholder="400001"
              readOnly={isBasicReadOnly}
            />

            <InputField
              label="Source"
              icon={Link}
              value={form.source}
              onChange={handleChange("source")}
              placeholder="LinkedIn / Referral / Website"
              readOnly={isBasicReadOnly}
            />
          </div>
        </div>

        {/* PROFESSIONAL DETAILS */}
        <div className="max-w-8xl mx-auto rounded-2xl overflow-hidden mt-6 bg-white border border-border transition">
          <div className="relative overflow-hidden border-b border-slate-200 bg-white px-8 py-6">
            <div className="absolute top-0 right-0 h-32 w-32 rounded-full bg-blue-100 blur-3xl opacity-60"></div>

            <div className="relative flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 shadow-sm">
                <Briefcase className="text-blue-600" size={28} />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  Professional Details
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Work & experience information
                </p>
              </div>
            </div>
          </div>
          <div className="px-8 pt-4">
            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2">
              <Briefcase size={16} />
              You can update professional details below
            </div>
          </div>
          <div className="p-8 grid md:grid-cols-3 gap-6">
            <InputField
              label="Current Location"
              icon={MapPin}
              value={form.currentLocation}
              onChange={handleChange("currentLocation")}
              placeholder="Enter Current Location"
              error={errors.currentLocation}
            />

            <InputField
              label="Qualification"
              icon={GraduationCap}
              value={form.qualification}
              onChange={handleChange("qualification")}
              placeholder="Enter Qualification"
              error={errors.qualification}
            />

            <InputField
              label="Total Experience"
              icon={Briefcase}
              value={form.totalExperience}
              onChange={handleChange("totalExperience")}
              placeholder="0"
              error={errors.totalExperience}
            />

            <InputField
              label="Current Company"
              icon={Building}
              value={form.currentCompany}
              onChange={handleChange("currentCompany")}
              placeholder="Enter Current Company"
              error={errors.currentCompany}
            />

            <InputField
              label="Designation"
              icon={Award}
              value={form.designation}
              onChange={handleChange("designation")}
              placeholder="Enter Designation"
              error={errors.designation}
            />

            <InputField
              label="Department"
              icon={Briefcase}
              value={form.department}
              onChange={handleChange("department")}
              placeholder="Enter Department"
              error={errors.department}
            />

            <InputField
              label="Skills"
              icon={ClipboardList}
              value={form.skills}
              onChange={handleChange("skills")}
              placeholder="Skill-1, Skill-2, Skill-3, ..."
              error={errors.skills}
            />

            <InputField
              label="Interested In"
              icon={Target}
              value={form.appliedFor}
              onChange={handleChange("appliedFor")}
              placeholder="Interested In"
              error={errors.appliedFor}
            />

            <InputField
              label="Preferred Location"
              icon={MapPin}
              value={form.preferredLocation}
              onChange={handleChange("preferredLocation")}
              placeholder="Enter Preferred Location"
              error={errors.preferredLocation}
            />

            <InputField
              label="Current CTC"
              icon={CreditCard}
              value={form.currentCTC}
              onChange={handleCTCChange("currentCTC")}
              placeholder="0,00,000"
              error={errors.currentCTC}
            />

            <InputField
              label="Expected CTC"
              icon={CreditCard}
              value={form.expectedCTC}
              onChange={handleCTCChange("expectedCTC")} // ✅ FIXED
              placeholder="0,00,000"
              error={errors.expectedCTC}
            />

            <InputField
              label="Reason of Leaving"
              icon={ClipboardList}
              value={form.reasonOfLeaving}
              onChange={handleChange("reasonOfLeaving")} // ✅ FIXED
              placeholder="Enter Reason of Leaving"
              error={errors.reasonOfLeaving}
            />

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700">
                Remark <span className="text-red-500">*</span>
              </label>

              <textarea
                value={form.remark || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    remark: e.target.value,
                  }))
                }
                rows={4}
                placeholder="Enter remark"
                className={`
      w-full rounded-xl px-3 py-2
      text-sm outline-none resize-none
      focus:ring-2 focus:ring-primary/30
      ${errors.remark ? "border border-red-500" : "border border-border"}
    `}
              />

              {errors.remark && (
                <p className="text-xs text-red-500">{errors.remark}</p>
              )}
            </div>
          </div>
          <div className="px-8 pb-8">
            {/* RESUME */}
            <div className="md:col-span-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                    <FileText className="text-red-600" size={18} />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-800">
                        Resume
                      </h3>

                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium border
              ${
                form.resumePath
                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                  : "bg-amber-50 text-amber-700 border-amber-100"
              }`}
                      >
                        {form.resumePath ? "Uploaded" : "Required"}
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 mt-1">
                      Upload or manage candidate resume
                    </p>
                  </div>
                </div>

                {/* Existing Resume */}
                {form.resumePath && (
                  <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                        <FileCheck size={16} className="text-emerald-600" />
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700">
                          Existing resume attached
                        </p>

                        <p className="text-xs text-slate-500 mt-0.5">
                          Candidate resume is currently available
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setOpenResumeDrawer(true)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 transition shrink-0"
                    >
                      <Eye size={14} />
                      View
                    </button>
                  </div>
                )}

                {/* Actions */}
                {form.resumePath && (
                  <div className="mt-4 flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={keepExistingResume}
                        onChange={() => {
                          setKeepExistingResume(true);

                          setForm((prev) => ({
                            ...prev,
                            resumeFile: null,
                          }));
                        }}
                      />

                      <span className="text-sm text-slate-700">
                        Keep Existing
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!keepExistingResume}
                        onChange={() => setKeepExistingResume(false)}
                      />

                      <span className="text-sm text-slate-700">Upload New</span>
                    </label>
                  </div>
                )}

                {/* Upload */}
                {(!form.resumePath || !keepExistingResume) && (
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
                    <label>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            resumeFile: e.target.files[0],
                          }))
                        }
                      />

                      <span className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition">
                        <Upload size={15} />
                        {form.resumePath
                          ? "Upload New Resume"
                          : "Choose Resume"}
                      </span>
                    </label>

                    <div className="text-xs text-slate-500">
                      PDF only • Max 2 MB
                    </div>

                    {form.resumeFile && (
                      <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium">
                        <FileCheck size={15} />
                        {form.resumeFile.name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Button */}
          <div className="flex justify-end px-8 pb-6">
            <button
              onClick={handleUpdate}
              disabled={loading}
              className={`px-8 py-2.5 rounded-lg font-medium text-white shadow-sm transition duration-200 ease-out transform-gpu
                ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed opacity-70"
                    : "bg-primary hover:shadow-md hover:shadow-primary/30 hover:scale-[1.015]"
                }
              `}
            >
              {loading ? "Updating..." : "Update Lead"}
            </button>
          </div>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}
      <ViewResumeDrawer
        open={openResumeDrawer}
        onClose={() => setOpenResumeDrawer(false)}
        resumeUrl={form.resumePath}
        candidateName={form.name}
      />
    </>
  );
};

export default EditLead;
